<script setup lang="ts">
import { nextTick, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps<{
  length?: number;
  disabled?: boolean;
  autofocus?: boolean;
}>();

const emit = defineEmits<{
  update: [code: string];
  complete: [code: string];
}>();

const { t } = useI18n();

const length = () => props.length ?? 6;
const digits = ref<string[]>(Array.from({ length: length() }, () => ""));
const inputs = ref<Array<HTMLInputElement | null>>([]);

function assignRef(index: number, el: Element | null): void {
  inputs.value[index] = el as HTMLInputElement | null;
}

function currentValue(): string {
  return digits.value.join("");
}

function focusIndex(index: number): void {
  const clamped = Math.max(0, Math.min(index, length() - 1));
  void nextTick(() => {
    inputs.value[clamped]?.focus();
    inputs.value[clamped]?.select();
  });
}

function onInput(event: Event, index: number): void {
  const target = event.target as HTMLInputElement;
  const cleaned = target.value.replace(/\D+/g, "");

  if (cleaned.length <= 1) {
    digits.value[index] = cleaned;
    target.value = cleaned;
    if (cleaned && index < length() - 1) {
      focusIndex(index + 1);
    }
  } else {
    distribute(cleaned, index);
  }

  const value = currentValue();
  emit("update", value);
  if (value.length === length()) {
    emit("complete", value);
  }
}

function distribute(source: string, startIndex: number): void {
  const cap = length();
  for (let i = 0; i < source.length && startIndex + i < cap; i++) {
    const ch = source[i] ?? "";
    digits.value[startIndex + i] = ch;
    const input = inputs.value[startIndex + i];
    if (input) input.value = ch;
  }
  const lastFilled = Math.min(startIndex + source.length, cap) - 1;
  focusIndex(lastFilled === cap - 1 ? lastFilled : lastFilled + 1);
}

function onKeydown(event: KeyboardEvent, index: number): void {
  if (event.key === "Backspace") {
    if (digits.value[index]) {
      digits.value[index] = "";
      emit("update", currentValue());
      return;
    }
    event.preventDefault();
    if (index > 0) {
      digits.value[index - 1] = "";
      emit("update", currentValue());
      focusIndex(index - 1);
    }
    return;
  }

  if (event.key === "ArrowLeft" && index > 0) {
    event.preventDefault();
    focusIndex(index - 1);
  } else if (event.key === "ArrowRight" && index < length() - 1) {
    event.preventDefault();
    focusIndex(index + 1);
  }
}

function onPaste(event: ClipboardEvent, index: number): void {
  const text = event.clipboardData?.getData("text") ?? "";
  const cleaned = text.replace(/\D+/g, "");
  if (!cleaned) return;
  event.preventDefault();
  distribute(cleaned, index);
  const value = currentValue();
  emit("update", value);
  if (value.length === length()) {
    emit("complete", value);
  }
}

function clear(): void {
  digits.value = Array.from({ length: length() }, () => "");
  inputs.value.forEach((input) => {
    if (input) input.value = "";
  });
  emit("update", "");
  focusIndex(0);
}

function fill(code: string): void {
  const cleaned = code.replace(/\D+/g, "").slice(0, length());
  if (cleaned.length === 0) return;
  distribute(cleaned, 0);
  const value = currentValue();
  emit("update", value);
  if (value.length === length()) {
    emit("complete", value);
  }
}

defineExpose({ clear, focus: () => focusIndex(0), fill });

watch(
  () => props.autofocus,
  (shouldFocus) => {
    if (shouldFocus) focusIndex(0);
  },
  { immediate: true },
);
</script>

<template>
  <fieldset class="otp-digits" :disabled="disabled">
    <legend class="visually-hidden">{{ t("otp.heading") }}</legend>
    <input
      v-for="(_, i) in digits"
      :key="i"
      :ref="(el) => assignRef(i, el as Element | null)"
      class="otp-digits__box"
      type="text"
      inputmode="numeric"
      autocomplete="one-time-code"
      maxlength="6"
      :value="digits[i]"
      :aria-label="t('otp.digitLabel', { n: i + 1 })"
      @input="(event) => onInput(event, i)"
      @keydown="(event) => onKeydown(event, i)"
      @paste="(event) => onPaste(event, i)"
    />
  </fieldset>
</template>

<style scoped>
.otp-digits {
  display: flex;
  gap: var(--space-2);
  border: none;
  padding: 0;
  margin: 0;
  justify-content: space-between;
}

.otp-digits__box {
  flex: 1;
  min-width: 0;
  width: 100%;
  height: 56px;
  text-align: center;
  font-size: var(--font-size-xl);
  font-weight: 600;
  background-color: var(--color-bg);
  border: 1px solid var(--color-border);
  border-radius: var(--radius);
  color: var(--color-text);
  transition: border-color var(--transition-fast);
}

.otp-digits__box:focus {
  border-color: var(--color-primary);
  outline: none;
  box-shadow: 0 0 0 3px var(--color-primary-muted);
}

.otp-digits__box:disabled {
  opacity: 0.6;
}
</style>
